// Deterministic region-aware packer. No DOM: text measurement is injected.
//
// packScene(plan, measure, opts) -> { panel, anchor, regions, boxes, connectors }
//   measure(text, fontPx, fontKey) -> width in px
//
// Every placed thing is a `box` {x,y,w,h,kind,...}. Chunks become a text
// box with laid-out lines; accent symbols become square image boxes. Boxes are
// placed greedily: candidates sampled on a grid inside the region polygon,
// scored by closeness to the region's flow anchor, first non-overlapping wins.
// Never moves a placed box (the live solver inherits this rule).

import { PANEL, getTemplate, regionPolygon } from './templates.js';
import { NO_REACH, motifReach } from './motifs.js';
import { textBoxReach } from './motifArt.js';

// A mooded chunk (TT-1269) paints outside its text rect: rays, curls, an
// arrowhead. `chunkMotif` is the motif a framed chunk carries (a caption
// never does); `chunkReach` is that paint on each side — from the painted
// piece when the host has the art, else the drawn motif — which inflates
// the rect that is placed so neighbours stay clear.
export const chunkMotif = (ch) => ((ch.container ?? true) && ch.mood) || undefined;
export const chunkReach = textBoxReach;
export const inflate = (sz, p, k = 1) => ({ w: (sz.w + p.left + p.right) * k, h: (sz.h + p.top + p.bottom) * k });
// A mood is a stand-in for the plain box, taken only when its paint fits
// where the chunk belongs at full size and clear of the anchor art's core
// — the middle of the symbol the frame is built round (board art is
// square; `anchor.h` is its side). A sun on the summit hid the mountain.
// Otherwise the chunk goes down plain, as it always could: a scribe never
// shrinks or moves the text to make room for a decoration. The core comes
// back as an obstacle list (empty without an anchor) to add to the placed
// rects for that one try. A chunk whose text leaves its section anyway (it
// would not fit plain either) takes the paint along if it fits at full size
// wherever it lands: the paint never causes the move. The live solver
// (live.js) seats in the same order.
const ANCHOR_CORE = 0.6;
export function anchorCore(anchor) {
  if (!anchor) return [];
  const s = anchor.h * ANCHOR_CORE;
  return [{ x: anchor.x - s / 2, y: anchor.y - s / 2, w: s, h: s }];
}

// Type ramp (relative to body). Scribe hierarchy: title ≫ subheader ≫ quote ≫ body.
export const CLASS_STYLE = {
  title:     { scale: 2.1, font: 'title',  color: 'ink',    caps: true  },
  subheader: { scale: 1.5, font: 'title',  color: 'accent', caps: true  },
  header:    { scale: 1.3, font: 'title',  color: 'accent', caps: true  },
  quote:     { scale: 1.15, font: 'quote', color: 'accent2', caps: false },
  decision:  { scale: 1.05, font: 'body',  color: 'ink',    caps: false, bullet: '✓' },
  action:    { scale: 1.05, font: 'body',  color: 'ink',    caps: false, bullet: '→' },
  question:  { scale: 1.05, font: 'body',  color: 'ink',    caps: false, bullet: '?' },
  risk:      { scale: 1.05, font: 'body',  color: 'ink',    caps: false, bullet: '!' },
  number:    { scale: 1.9, font: 'title',  color: 'accent', caps: false },
  body:      { scale: 1.0, font: 'body',   color: 'ink',    caps: false, bullet: '•' },
  metaphor:  { scale: 1.0, font: 'quote',  color: 'ink',    caps: false },
};

export const SYMBOL_SCALE = { s: 0.12, m: 0.16, l: 0.24, xl: 0.5 }; // fraction of panel height

// shared sizing rules (static packer and live solver must agree)
export const chunkMaxW = (importance, panel) => panel.w * (importance >= 0.8 ? 0.30 : 0.23);
export const stickerSize = panel => SYMBOL_SCALE.s * panel.h;
// last-resort spill target: any free space on the board (a scribe fills gaps)
export function boardPoly(panel) {
  const m = panel.h * 0.02;
  return [[m, m], [panel.w - m, m], [panel.w - m, panel.h - m], [m, panel.h - m]];
}
// title block — top-left, the scribe's pre-lettered skeleton
export function layoutTitle(title, bodyPx, panel, measure) {
  const items = [{ class: 'title', text: title?.text || '' }].concat(title?.date ? [{ class: 'metaphor', text: title.date }] : []);
  const lay = layoutChunk({ items }, bodyPx, panel.w * 0.36, measure);
  return { ...lay, x: panel.w * 0.03, y: panel.h * 0.03 };
}

// ---- geometry -------------------------------------------------------------
export function pointInPoly([px, py], poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
export function rectInPoly(r, poly) {
  return [[r.x, r.y], [r.x + r.w, r.y], [r.x, r.y + r.h], [r.x + r.w, r.y + r.h], [r.x + r.w / 2, r.y + r.h / 2]]
    .every(p => pointInPoly(p, poly));
}
export function overlaps(a, b, m) {
  return !(a.x + a.w + m <= b.x || b.x + b.w + m <= a.x || a.y + a.h + m <= b.y || b.y + b.h + m <= a.y);
}
export function bbox(poly) {
  const xs = poly.map(p => p[0]), ys = poly.map(p => p[1]);
  const x0 = Math.min(...xs), y0 = Math.min(...ys), x1 = Math.max(...xs), y1 = Math.max(...ys);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
}
export function flowAnchor(region, bb) {
  switch (region.flow) {
    case 'top':  return [bb.cx, bb.y];
    case 'left': return [bb.x, bb.cy];
    default:     return [bb.cx, bb.cy];
  }
}

// ---- text layout ----------------------------------------------------------
function wrap(text, fontPx, fontKey, maxW, measure) {
  const words = text.split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    const t = cur ? cur + ' ' + w : w;
    if (measure(t, fontPx, fontKey) <= maxW || !cur) cur = t;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  return lines;
}

export function layoutChunk(chunk, bodyPx, maxW, measure) {
  const pad = bodyPx * 0.9;
  const innerW = maxW - pad * 2;
  const lines = [];
  let y = pad;
  const pushItem = (cls, text, extraGap = 0) => {
    const st = CLASS_STYLE[cls] || CLASS_STYLE.body;
    const px = bodyPx * st.scale;
    const t = st.caps ? text.toUpperCase() : text;
    const bullet = st.bullet ? st.bullet + ' ' : '';
    const wrapped = wrap(bullet + t, px, st.font, innerW, measure);
    wrapped.forEach((ln, i) => {
      y += px * 1.05;
      lines.push({ text: ln, px, font: st.font, color: st.color, cls, x: pad, y, itemStart: i === 0 });
    });
    y += px * 0.28 + extraGap;
  };
  if (chunk.header) pushItem('header', chunk.header, bodyPx * 0.2);
  for (const it of chunk.items) pushItem(it.class || 'body', it.text);
  const w = lines.length ? Math.min(maxW, Math.max(...lines.map(l => l.x + measure(l.text, l.px, l.font) + pad))) : pad * 2;
  return { w, h: y + pad * 0.6, lines };
}

// How far a corner sticker tucks into its chunk: TUCK_X of its width over the
// side, all but TUCK_Y of its height down over the top edge.
const TUCK_X = 0.4, TUCK_Y = 0.62;
// A sticker sits on its own chunk's corner: within `gap` of the chunk (one
// adopted from a retired chunk, or whose chunk was re-seated, floated where
// it was — TT-1266) and over it by at most that corner, with a little slack
// (a held sticker that a re-laid chunk has swallowed sits on its text
// instead, TT-1229). Either way it must be re-placed.
export function onCorner(r, box, gap) {
  if (!overlaps(r, box, -gap)) return false;
  const ix = Math.min(r.x + r.w, box.x + box.w) - Math.max(r.x, box.x);
  const iy = Math.min(r.y + r.h, box.y + box.h) - Math.max(r.y, box.y);
  return ix <= 0 || iy <= 0 || ix * iy <= (TUCK_X * (1 - TUCK_Y) + 0.05) * r.w * r.h;
}

// Place a symbol next to the chunk it illustrates: sticker on the top-right
// corner first (overlapping its own box a little, like a scribe drawing the
// icon into the frame), then beside, then above/below.
export function placeNear(box, size, placed, panel, margin) {
  const own = placed.filter(p => p !== box);
  const gap = margin;
  const cands = [
    { x: box.x + box.w - size * TUCK_X, y: box.y - size * TUCK_Y },        // top-right sticker (overlaps the corner, not the header)
    { x: box.x - size * (1 - TUCK_X), y: box.y - size * TUCK_Y },          // top-left sticker
    { x: box.x + box.w + gap, y: box.y },                          // right
    { x: box.x - size - gap, y: box.y },                           // left
    { x: box.x + box.w - size, y: box.y - size - gap },            // above
    { x: box.x + box.w - size, y: box.y + box.h + gap },           // below
    { x: box.x + box.w + gap, y: box.y + box.h - size },           // right, low
    { x: box.x - size - gap, y: box.y + box.h - size },            // left, low
    { x: box.x, y: box.y - size - gap },                           // above, left end
    { x: box.x, y: box.y + box.h + gap },                          // below, left end
  ];
  for (const c of cands) {
    const r = { x: c.x, y: c.y, w: size, h: size };
    if (r.x < 0 || r.y < 0 || r.x + r.w > panel.w || r.y + r.h > panel.h) continue;
    if (own.some(p => overlaps(r, p, margin * 0.5))) continue;
    return r;
  }
  return null;
}

// ---- placement ------------------------------------------------------------
export function place(box, poly, placed, region, margin, step, anchor = null) {
  // a degenerate box or step would make the grid loop spin forever
  if (!(box.w > 0 && box.h > 0 && step > 0) || !isFinite(box.w + box.h)) return null;
  const bb = bbox(poly);
  const [ax, ay] = anchor || flowAnchor(region, bb);
  const cands = [];
  if (!(bb.w > 0 && bb.h > 0) || !isFinite(bb.w + bb.h)) return null;
  for (let y = bb.y; y + box.h <= bb.y + bb.h; y += step)
    for (let x = bb.x; x + box.w <= bb.x + bb.w; x += step) {
      const r = { x, y, w: box.w, h: box.h };
      if (!rectInPoly(r, poly)) continue;
      const cx = x + box.w / 2, cy = y + box.h / 2;
      cands.push({ r, d: Math.hypot(cx - ax, cy - ay) + (region.flow === 'top' ? y * 0.5 : 0) });
    }
  cands.sort((a, b) => a.d - b.d);
  for (const c of cands) if (!placed.some(p => overlaps(c.r, p, margin))) return c.r;
  return null;
}

// Fill-rate pass: a scribe writes bigger when the board has room. Re-pack with
// larger type until coverage reaches the target or something stops fitting.
export function packScene(plan, measure, opts = {}) {
  const panel = opts.panel || PANEL;
  const base = opts.bodyPx || panel.h * 0.024;
  const target = opts.fillTarget ?? 0.55;
  let best = packOnce(plan, measure, { ...opts, panel, bodyPx: base });
  // over capacity: write smaller (down to 0.78×) before dropping anything
  for (let mul = 0.92; best.dropped.length && mul >= 0.78; mul *= 0.92) {
    const next = packOnce(plan, measure, { ...opts, panel, bodyPx: base * mul });
    if (next.dropped.length < best.dropped.length) best = next;
    if (!best.dropped.length) break;
  }
  for (let mul = 1.12; mul <= 1.5; mul *= 1.12) {
    if (best.coverage >= target || best.dropped.length) break;
    const next = packOnce(plan, measure, { ...opts, panel, bodyPx: base * mul });
    if (next.dropped.length) break;
    best = next;
  }
  return best;
}

function packOnce(plan, measure, opts) {
  const panel = opts.panel;
  const tpl = getTemplate(plan.archetype, plan.metaphor);
  const bodyPx = opts.bodyPx;
  const margin = bodyPx * 0.6;
  const step = Math.max(8, bodyPx * 0.5);

  const regions = tpl.regions.map(r => ({ ...r, poly: regionPolygon(r, panel) }));
  const byName = Object.fromEntries(regions.map(r => [r.name, r]));
  const anywhere = { name: '*', role: 'any', flow: 'center', poly: boardPoly(panel) };
  const spillRegions = [...regions, anywhere];
  const placed = [];
  const boxes = [];
  const dropped = [];

  // 1. title block — always first
  if (plan.title) {
    const t = layoutTitle(plan.title, bodyPx, panel, measure);
    const r = { x: t.x, y: t.y, w: t.w, h: t.h };
    placed.push(r); boxes.push({ ...r, kind: 'title', lines: t.lines });
  }

  // 2. anchor symbol (background role) — not a collision participant
  const anchorSym = plan.symbols.find(s => s.role === 'anchor');
  const anchor = anchorSym && tpl.anchor ? {
    symbolId: anchorSym.symbol_id, x: tpl.anchor.x * panel.w, y: tpl.anchor.y * panel.h,
    h: (anchorSym.scale === 'xl' ? tpl.anchor.h : SYMBOL_SCALE[anchorSym.scale || 'xl']) * panel.h,
    opacity: anchorSym.opacity ?? tpl.anchor.opacity,
  } : null;
  const core = anchorCore(anchor);

  // 3. chunks by importance (desc) — most important gets the best spot
  const chunks = [...plan.chunks].sort((a, b) => (b.importance || 0) - (a.importance || 0));
  const usedSymbols = new Set();
  for (const ch of chunks) {
    const region = byName[ch.region] || regions[0];
    const bb = bbox(region.poly);
    const baseW = Math.min(bb.w * 0.92, chunkMaxW(ch.importance, panel));
    // Retry ladder: wider (shorter) box first, then a smaller type size — a
    // scribe squeezes before they move a chunk somewhere it doesn't belong.
    const ladder = [[1.0, 1.0], [1.4, 1.0], [1.0, 0.85], [1.4, 0.85], [1.8, 0.8]];
    // A mooded chunk tries its mood first — full type, in its own region,
    // placed with its reach (the paint outside the text) and clear of the
    // anchor's core (anchorCore), then its type one notch smaller (0.9,
    // 0.8: TT-1269 loosening 4, the one bend of "never shrink the text for
    // a decoration") — then the ladder plain like any other.
    const mood = chunkMotif(ch);
    const rungs = (mood ? [[1.0, 1.0, mood], [1.4, 1.0, mood], [1.0, 0.9, mood], [1.0, 0.8, mood]] : []).concat(ladder.map(([w, f]) => [w, f, null]));
    const lays = {};   // a rung's layout, once (the mood rungs and the plain ones share two)
    const layAt = (fMul, w) => (lays[`${fMul}:${w}`] ||= layoutChunk(ch, bodyPx * fMul, w, measure));
    let r = null, lay = null, pad = NO_REACH, motif = null;
    for (const [wMul, fMul, m] of rungs) {
      lay = layAt(fMul, Math.min(bb.w * 0.96, baseW * wMul));
      pad = m ? chunkReach(m, lay, opts.textBoxArt) : NO_REACH;
      r = place(inflate(lay, pad), region.poly, m ? placed.concat(core) : placed, region, margin, step);
      if (r) { motif = m; break; }
    }
    if (!r) { // spill: any region, the same rungs — the paint first when the text leaves anyway, then the squeeze ladder plain
      // (the decoration never causes a move; a box carrying its paint keeps off the core wherever it lands)
      outer: for (const [wMul, fMul, m] of rungs) {
        lay = layAt(fMul, baseW * wMul);
        pad = m ? chunkReach(m, lay, opts.textBoxArt) : NO_REACH;
        const blockers = m ? placed.concat(core) : placed;
        for (const alt of spillRegions) { r = place(inflate(lay, pad), alt.poly, blockers, alt, margin, step); if (r) { motif = m; break outer; } }
      }
    }
    if (!r) { dropped.push(ch.id); continue; }
    placed.push(r);
    // the box is the text rect inside the placed (inflated) one
    const box = { x: r.x + pad.left, y: r.y + pad.top, w: lay.w, h: lay.h };
    boxes.push({ ...box, kind: 'chunk', id: ch.id, hue: ch.hue || 'accent', lines: lay.lines, region: region.name, container: ch.container ?? true, ...(motif ? { motif } : {}) });
    // symbols that illustrate this chunk go right next to it — one drawing per
    // symbol per board (chunks are visited by importance, so the most
    // important claim wins), and never the anchor art again in miniature
    for (const symbolId of ch.symbol_ids || []) {
      if (usedSymbols.has(symbolId) || symbolId === anchorSym?.symbol_id) continue;
      usedSymbols.add(symbolId);
      const size = SYMBOL_SCALE.s * panel.h;
      const sr = placeNear(r, size, placed, panel, margin);
      if (!sr) { dropped.push('sym:' + symbolId + '@' + ch.id); continue; }
      placed.push(sr);
      boxes.push({ ...sr, kind: 'symbol', symbolId, region: region.name, of: ch.id });
    }
  }

  // 4. free accent symbols (legacy plans) — same uniqueness rule
  for (const s of plan.symbols.filter(s => s.role !== 'anchor')) {
    if (usedSymbols.has(s.symbol_id) || s.symbol_id === anchorSym?.symbol_id) continue;
    usedSymbols.add(s.symbol_id);
    const region = byName[s.region] || regions[0];
    // try the requested scale, then shrink one step at a time before giving up
    const ladder = ['xl', 'l', 'm', 's'];
    let r = null;
    for (let i = ladder.indexOf(s.scale || 'm'); i < ladder.length && !r; i++) {
      const size = SYMBOL_SCALE[ladder[i]] * panel.h;
      r = place({ w: size, h: size }, region.poly, placed, region, margin, step);
    }
    if (!r) { // spill small into any region rather than vanish
      const size = SYMBOL_SCALE.s * panel.h;
      for (const alt of spillRegions) { r = place({ w: size, h: size }, alt.poly, placed, alt, margin, step); if (r) break; }
    }
    if (!r) { dropped.push('sym:' + s.symbol_id); continue; }
    placed.push(r);
    boxes.push({ ...r, kind: 'symbol', symbolId: s.symbol_id, region: region.name });
  }

  // 5. connectors resolve to boxes; drop ones between touching neighbours
  //    (a scribe wouldn't draw an arrow across a 10px gap)
  const byId = Object.fromEntries(boxes.filter(b => b.id).map(b => [b.id, b]));
  //    ...or ones whose straight path would cross a third box (no arrows through text)
  const center = b => [b.x + b.w / 2, b.y + b.h / 2];
  const segHitsRect = ([x0, y0], [x1, y1], r) => {
    for (let t = 0.08; t < 0.92; t += 0.04) {
      const x = x0 + (x1 - x0) * t, y = y0 + (y1 - y0) * t;
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return true;
    }
    return false;
  };
  const connectors = (plan.connectors || [])
    .map(c => ({ ...c, a: byId[c.from], b: byId[c.to] }))
    .filter(c => c.a && c.b && !overlaps(c.a, c.b, margin * 3))
    .filter(c => !boxes.some(b => b !== c.a && b !== c.b && b.kind !== 'symbol' && segHitsRect(center(c.a), center(c.b), b)));

  const coverage = placed.reduce((a, b) => a + b.w * b.h, 0) / (panel.w * panel.h);
  return { panel, anchor, regions, boxes, connectors, dropped, bodyPx, coverage };
}
